/**
 * Marketing Hub v2 — canonical IA: workspace modes, pages, and sub-items.
 *
 * URLs follow the spec's `/marketing/<mode>/<page>` shape. Sub-items either
 * deep-link to a tab/state within the page (via `?tab=` query param) or
 * scroll to a section anchor. The page itself is responsible for reading
 * `useSearchParams().get('tab')` and updating its internal active tab state.
 *
 * `OLD_TO_NEW` powers 301-equivalent <Navigate replace /> redirects in App.js
 * so external links + bookmarks at the previous flat URLs keep working.
 */

export const MARKETING_IA = {
  plan: {
    label: "Plan",
    caption: "strategy, targeting, scheduling",
    landing: "/marketing/plan/overview",
    pages: [
      { id: "overview", label: "Overview", path: "/marketing/plan/overview", icon: "LayoutDashboard" },
      {
        id: "calendar", label: "Calendar", path: "/marketing/plan/calendar", icon: "CalendarDays",
        subItems: [
          { label: "Month", path: "/marketing/plan/calendar?view=month" },
          { label: "Week", path: "/marketing/plan/calendar?view=week" },
        ],
      },
      {
        id: "segments", label: "Segments", path: "/marketing/plan/segments", icon: "Users",
        subItems: [
          { label: "All segments", path: "/marketing/plan/segments" },
          { label: "Builder", path: "/marketing/plan/segments?tab=builder" },
        ],
      },
      {
        id: "campaigns", label: "Campaigns", path: "/marketing/plan/campaigns", icon: "Mail",
        subItems: [
          { label: "Active", path: "/marketing/plan/campaigns?filter=active" },
          { label: "Drafts", path: "/marketing/plan/campaigns?filter=draft" },
          { label: "Sent", path: "/marketing/plan/campaigns?filter=sent" },
        ],
      },
    ],
  },
  create: {
    label: "Create",
    caption: "drafts, templates, brand assets",
    landing: "/marketing/create/studio",
    pages: [
      { id: "studio", label: "Content Studio", path: "/marketing/create/studio", icon: "PenSquare" },
      { id: "templates", label: "Template Library", path: "/marketing/create/templates", icon: "FolderOpen" },
      {
        id: "library", label: "Brand Library", path: "/marketing/create/library", icon: "Palette",
        subItems: [
          { label: "Assets", path: "/marketing/create/library?tab=assets" },
          { label: "Brand kit", path: "/marketing/create/library?tab=kit" },
        ],
      },
      { id: "assets", label: "Asset Generator", path: "/marketing/create/assets", icon: "Image" },
    ],
  },
  distribute: {
    label: "Distribute",
    caption: "publish, schedule, integrate",
    landing: "/marketing/distribute/channels",
    pages: [
      {
        id: "channels", label: "Channels", path: "/marketing/distribute/channels", icon: "Share2",
        subItems: [
          { label: "All", path: "/marketing/distribute/channels" },
          { label: "LinkedIn", path: "/marketing/distribute/channels?channel=linkedin" },
          { label: "Email", path: "/marketing/distribute/channels?channel=email" },
          { label: "Blog", path: "/marketing/distribute/channels?channel=blog" },
        ],
      },
      { id: "approvals", label: "Approvals", path: "/marketing/distribute/approvals", icon: "CheckCircle2" },
      { id: "connections", label: "Connections", path: "/marketing/distribute/connections", icon: "Plug" },
    ],
  },
  measure: {
    label: "Measure",
    caption: "performance, AI search, reports",
    landing: "/marketing/measure/geo-seo",
    pages: [
      { id: "analytics", label: "Analytics", path: "/marketing/measure/analytics", icon: "BarChart2" },
      {
        id: "geo-seo", label: "GEO / SEO Engine", path: "/marketing/measure/geo-seo", icon: "Compass",
        subItems: [
          { label: "Citation share", path: "/marketing/measure/geo-seo?tab=citations" },
          { label: "Tracked queries", path: "/marketing/measure/geo-seo?tab=queries" },
          { label: "Schema / JSON-LD", path: "/marketing/measure/geo-seo?tab=schema" },
          { label: "llms.txt + crawlers", path: "/marketing/measure/geo-seo?tab=llms" },
        ],
      },
      { id: "reports", label: "Reports", path: "/marketing/measure/reports", icon: "FileText" },
    ],
  },
  agents: {
    label: "Agents",
    caption: "agents, workflows, audit",
    landing: "/marketing/agents/list",
    pages: [
      { id: "list", label: "Agents", path: "/marketing/agents/list", icon: "Bot" },
      { id: "workflows", label: "Workflows", path: "/marketing/agents/workflows", icon: "Workflow" },
      {
        id: "runs", label: "Run History", path: "/marketing/agents/runs", icon: "History",
        subItems: [
          { label: "All runs", path: "/marketing/agents/runs?filter=all" },
          { label: "Completed", path: "/marketing/agents/runs?filter=completed" },
          { label: "Awaiting approval", path: "/marketing/agents/runs?filter=awaiting_approval" },
          { label: "Failed", path: "/marketing/agents/runs?filter=failed" },
        ],
      },
    ],
  },
};

export const MODE_ORDER = ["plan", "create", "distribute", "measure", "agents"];

/** Old flat path → new mode/page path. Powers <Navigate replace /> redirects. */
export const OLD_TO_NEW = {
  "/marketing/overview":        "/marketing/plan/overview",
  "/marketing/calendar":        "/marketing/plan/calendar",
  "/marketing/audience":        "/marketing/plan/segments",
  "/marketing/segments":        "/marketing/plan/segments",
  "/marketing/campaigns":       "/marketing/plan/campaigns",
  "/marketing/content-studio":  "/marketing/create/studio",
  "/marketing/brand-library":   "/marketing/create/library",
  "/marketing/asset-generator": "/marketing/create/assets",
  "/marketing/channels":        "/marketing/distribute/channels",
  "/marketing/approvals":       "/marketing/distribute/approvals",
  "/marketing/connections":     "/marketing/distribute/connections",
  "/marketing/analytics":       "/marketing/measure/analytics",
  "/marketing/geo-seo":         "/marketing/measure/geo-seo",
  "/marketing/reports":         "/marketing/measure/reports",
  "/marketing/agents":          "/marketing/agents/list",
  "/marketing/workflows":       "/marketing/agents/workflows",
  "/marketing/run-history":     "/marketing/agents/runs",
  // legacy v1 archived → reroute through new IA
  "/marketing/social":          "/marketing/distribute/channels",
  "/marketing/content":         "/marketing/create/studio",
  "/marketing/email-builder":   "/marketing/create/studio",
  "/marketing/social-designer": "/marketing/create/studio",
};

/** Reverse map — derive mode from any /marketing/<mode>/... path. */
export function getModeForPath(path) {
  // /marketing/<mode>/...
  const m = path.match(/^\/marketing\/([^/]+)/);
  if (m && MARKETING_IA[m[1]]) return m[1];
  // Legacy flat path? Use OLD_TO_NEW to resolve.
  for (const [old, fresh] of Object.entries(OLD_TO_NEW)) {
    if (path === old || path.startsWith(old + "/") || path.startsWith(old + "?")) {
      const m2 = fresh.match(/^\/marketing\/([^/]+)/);
      if (m2 && MARKETING_IA[m2[1]]) return m2[1];
    }
  }
  return "plan";
}
